import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const serviceSchema = buildServiceSchema(
  'Motorisation de volets roulants à Paris 15ᵉ',
  'Transformez vos volets roulants manuels en volets électriques dans le 15ᵉ. Installation moteur Somfy, Nice, Bubendorff, Simu. Devis gratuit. ☎ 01 87 66 81 55'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Motorisation volet roulant', item: 'https://fenetre-paris15.fr/motorisation-volet-roulant-paris-15/' },
]);

const faqs = [
  { q: `Peut-on motoriser n'importe quel volet roulant manuel ?`, a: `La grande majorité des volets roulants manuels (à manivelle ou à sangle) peuvent être motorisés, à condition que le coffre soit en bon état et que l'axe puisse recevoir un moteur tubulaire. Lors du diagnostic gratuit, DF PRO vérifie la faisabilité technique : état du tablier, dimensions du coffre, présence d'une arrivée électrique ou possibilité d'en créer une. Seuls les volets avec un coffre trop dégradé ou un tablier en fin de vie peuvent nécessiter un remplacement préalable.` },
  { q: `Faut-il des travaux électriques pour motoriser un volet roulant ?`, a: `Cela dépend de la configuration. Si une arrivée électrique existe déjà à proximité (prise, interrupteur), le raccordement est simple. Dans le cas contraire, nous pouvons soit tirer une ligne depuis le tableau électrique, soit vous proposer un moteur radio avec une alimentation sur prise ou un moteur solaire pour éviter toute maçonnerie. Nos techniciens vous conseillent la solution la plus adaptée.` },
  { q: `Quelle marque de moteur choisir ?`, a: `Nous installons et recommandons principalement Somfy, le leader français de la motorisation. Nice, Bubendorff et Simu sont également d'excellentes alternatives selon le poids du tablier et le type de pilotage souhaité. Le choix dépend de la taille du volet, du type d'utilisation (télécommande seule, domotique, smartphone) et de votre budget. Nous vous guidons pour sélectionner le moteur optimal.` },
  { q: `Combien coûte la motorisation d'un volet roulant ?`, a: `Le prix varie selon le type de moteur (filaire, radio, solaire), la marque choisie et la configuration du volet. À titre indicatif, une motorisation radio Somfy complète (moteur + télécommande) démarre autour de 280€ à 400€ par volet. Le devis est gratuit et fixé avant intervention — aucun supplément caché. Contactez-nous pour une estimation précise après diagnostic.` },
];

export default function MotorisationVoletPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="Motorisation Volet Roulant Paris 15 (75015) — Somfy & Toutes Marques | DF PRO"
        description="Motorisez vos volets roulants manuels dans le 15ᵉ : Somfy, Nice, Bubendorff, Simu. Pose de moteur, télécommande, devis gratuit. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/motorisation-volet-roulant-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, buildFaqSchema(faqs), serviceSchema)}
      />

      {/* Breadcrumb */}
      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Motorisation volet roulant</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark leading-tight mb-4">Motorisation de volets roulants à Paris 15ᵉ</h1>
              <p className="text-brand-text/70 text-base leading-relaxed mb-6">
                Donnez une seconde vie à vos volets roulants manuels. DF PRO transforme vos volets à manivelle ou à sangle en <strong>volets électriques</strong> modernes, silencieux et pilotables à distance. Intervention rapide dans tout le 15ᵉ, devis gratuit et sans engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-motorisation-hero.webp" alt="Motorisation de volet roulant Somfy avec télécommande — DF PRO Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi motoriser */}
      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4">Pourquoi motoriser ses volets roulants ?</h2>
          <p className="section-subtitle mb-10">
            Passer du volet manuel au volet électrique change votre quotidien bien au-delà du simple confort. Voici les bénéfices concrets.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-thumb-up-line', title: 'Confort au quotidien', desc: `Fini la manivelle ou la sangle à tirer chaque matin et chaque soir. Un appui sur la télécommande, et tous vos volets s'ouvrent ou se ferment en quelques secondes. Un confort qui change la vie, surtout quand on a plusieurs fenêtres.` },
              { icon: 'ri-timer-line', title: 'Gain de temps', desc: `Motoriser vos volets, c'est gagner plusieurs minutes chaque jour. En un geste, vous commandez l'ensemble des ouvertures de votre logement. Le temps gagné est considérable sur une année.` },
              { icon: 'ri-shield-check-line', title: 'Sécurité renforcée', desc: `Les moteurs programmables permettent la simulation de présence : vos volets s'ouvrent et se ferment automatiquement même quand vous êtes absent. Un vrai atout dissuasif contre les cambriolages.` },
              { icon: 'ri-heart-pulse-line', title: 'Accessibilité', desc: `Pour les personnes âgées ou à mobilité réduite, manipuler une manivelle ou tirer une sangle peut être pénible, voire impossible. La motorisation redonne autonomie et confort au quotidien.` },
              { icon: 'ri-temp-hot-line', title: 'Isolation améliorée', desc: `En motorisant votre volet, vous l'utilisez plus souvent. Résultat : meilleure isolation thermique l'hiver, protection solaire l'été, et jusqu'à 10% d'économies d'énergie selon l'ADEME.` },
              { icon: 'ri-home-heart-line', title: 'Valorisation du bien', desc: `Un logement équipé de volets roulants motorisés est plus attractif à la vente ou à la location. C'est un critère recherché qui valorise votre bien immobilier sans travaux lourds.` },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 md:p-6 card-hover">
                <div className="w-10 h-10 rounded-xl bg-brand-mint text-brand-accent flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quels volets motoriser + Types */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
            <div>
              <h2 className="section-title mb-4">Quels volets peut-on motoriser ?</h2>
              <p className="text-brand-text/70 leading-relaxed mb-4">
                La quasi-totalité des volets roulants manuels peuvent être motorisés. Que vous ayez un <strong>volet à manivelle</strong>, un <strong>volet à sangle</strong> ou un <strong>volet à tringle</strong>, le principe est le même : on remplace l'axe de commande manuelle par un moteur tubulaire qui vient se loger dans le tube d'enroulement.
              </p>
              <p className="text-brand-text/70 leading-relaxed mb-3">
                Nous motorisons les tabliers <strong>PVC</strong> comme <strong>aluminium</strong>, sur tous types de pose : volets traditionnels, coffre tunnel, bloc-baie ou volet rénovation. Avant toute intervention, DF PRO réalise un <strong>diagnostic gratuit</strong> pour vérifier la faisabilité technique et vous proposer la solution optimale.
              </p>
              <div className="aspect-[16/9] rounded-xl overflow-hidden mt-6">
                <img src="/images/dfpro-motorisation-types.webp" alt="Installation moteur tubulaire volet roulant par technicien DF PRO — Paris 15" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div>
              <h2 className="section-title mb-6">Types de motorisation</h2>
              <div className="space-y-5">
                <div className="bg-brand-mint/30 rounded-xl p-5">
                  <h3 className="font-heading font-bold text-brand-dark mb-2">Moteur filaire</h3>
                  <p className="text-brand-text/60 text-sm leading-relaxed">
                    Raccordé directement à un interrupteur mural, c'est la solution la plus économique. Idéal quand un câble électrique passe déjà à proximité du coffre. Fonctionnement simple : un interrupteur par volet. Pilotage possible en groupe avec un interrupteur général.
                  </p>
                </div>
                <div className="bg-brand-mint/30 rounded-xl p-5">
                  <h3 className="font-heading font-bold text-brand-dark mb-2">Moteur radio / télécommandé</h3>
                  <p className="text-brand-text/60 text-sm leading-relaxed">
                    C'est le <strong>choix le plus courant en rénovation</strong>. Le moteur intègre un récepteur radio, piloté par télécommande (individuelle ou centrale). Aucun câble entre l'interrupteur et le volet. Compatible domotique (Somfy TaHoma, Connexoon) pour pilotage depuis un smartphone, programmation horaire, scénarios.
                  </p>
                </div>
                <div className="bg-brand-mint/30 rounded-xl p-5">
                  <h3 className="font-heading font-bold text-brand-dark mb-2">Moteur solaire</h3>
                  <p className="text-brand-text/60 text-sm leading-relaxed">
                    Le moteur est alimenté par un panneau photovoltaïque discret fixé sur le coffre ou la façade. <strong>Aucun raccordement électrique</strong> nécessaire : idéal quand l'arrivée de courant est difficile ou impossible. Autonome, écologique, et éligible à certaines aides à la rénovation énergétique.
                  </p>
                </div>
              </div>
              <p className="text-brand-text/70 text-sm mt-5">
                Quel que soit le type de motorisation, le pilotage peut se faire par <strong>télécommande, interrupteur mural, ou smartphone</strong> (via une box domotique). Nous vous conseillons la solution la plus adaptée à votre logement et vos habitudes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Marques */}
      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4 text-center">Marques que nous installons</h2>
          <p className="section-subtitle mx-auto text-center mb-10 max-w-2xl">
            Nous travaillons avec les motoristes de référence pour garantir fiabilité et durabilité.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
            {[
              { name: 'Somfy', desc: 'Leader mondial' },
              { name: 'Nice', desc: 'Italien, haut de gamme' },
              { name: 'Bubendorff', desc: 'Français, robuste' },
              { name: 'Simu', desc: 'Français, fiable' },
              { name: 'Becker', desc: 'Allemand, précis' },
              { name: 'Profalux', desc: 'Français, connecté' },
            ].map((brand) => (
              <div key={brand.name} className="bg-white rounded-xl p-4 text-center card-hover">
                <span className="font-heading font-bold text-brand-dark text-sm">{brand.name}</span>
                <p className="text-brand-text/40 text-xs mt-1">{brand.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-brand-text/60 text-sm text-center max-w-2xl mx-auto">
            Le choix du moteur dépend du <strong>poids du tablier</strong>, du <strong>diamètre du tube d'enroulement</strong> et de la <strong>course du volet</strong>. Nos techniciens calculent le couple moteur nécessaire pour garantir une motorisation silencieuse et durable, sans sous-dimensionnement ni surcoût inutile.
          </p>
        </div>
      </section>

      {/* Déroulé intervention */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h2 className="section-title mb-6">Comment se passe une motorisation ?</h2>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Diagnostic et étude de faisabilité gratuite', desc: `Nous nous déplaçons à votre domicile dans le 15ᵉ pour inspecter le volet, mesurer le coffre, évaluer le poids du tablier et vérifier l'accès électrique.` },
                  { step: '2', title: 'Choix du moteur et devis', desc: `Sur la base du diagnostic, nous vous recommandons le moteur adapté et vous remettons un devis détaillé, gratuit et sans engagement.` },
                  { step: '3', title: "Pose et remplacement de l'axe", desc: `Le jour de l'intervention, nous déposons l'ancien mécanisme, installons le moteur tubulaire et remplaçons l'axe si nécessaire.` },
                  { step: '4', title: 'Réglage des fins de course', desc: `Le moteur est programmé pour s'arrêter exactement en position haute et basse. Ce réglage de précision protège le tablier et le moteur.` },
                  { step: '5', title: 'Programmation des télécommandes', desc: `Nous associons la ou les télécommandes, configurons les groupes si besoin, et vous expliquons le fonctionnement.` },
                  { step: '6', title: 'Test et nettoyage', desc: `Vérification complète du fonctionnement, nettoyage de la zone d'intervention, et pause café pour répondre à toutes vos questions.` },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-brand-accent text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">{item.step}</div>
                    <div>
                      <h3 className="font-heading font-bold text-brand-dark text-sm mb-0.5">{item.title}</h3>
                      <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-brand-gradientFrom to-brand-gradientTo rounded-xl p-6 md:p-8 text-white">
              <h2 className="font-heading font-bold text-xl mb-4">Tarifs et devis</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Le prix d'une motorisation varie selon le <strong>type de moteur</strong> (filaire, radio, solaire), la <strong>marque choisie</strong>, et la configuration du volet (taille du tablier, accessibilité du coffre).
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                À titre indicatif, une motorisation radio Somfy complète (moteur + télécommande) se situe autour de <strong>280€ à 400€ par volet</strong>. Le devis est <strong>gratuit, détaillé et à prix fixe</strong> — vous savez exactement combien vous allez payer avant qu'on intervienne.
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                Pour les immeubles ou appartements avec plusieurs volets, nous proposons des tarifs dégressifs. N'hésitez pas à nous consulter.
              </p>
              <Link to="/devis-fenetre-paris-15/" className="inline-flex items-center gap-2 bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
                <i className="ri-file-text-line"></i> Demander un devis gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8 text-center">Questions fréquentes — Motorisation</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-mint/30 transition-colors cursor-pointer">
                  <span className="font-heading font-bold text-brand-dark text-sm pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-brand-accent text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4">
                    <p className="text-brand-text/70 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + liens croisés */}
      <section className="py-12 md:py-16 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4">Prêt à motoriser vos volets ?</h2>
          <p className="section-subtitle mx-auto mb-6">DF PRO intervient dans tout le 15ᵉ pour moderniser vos volets roulants. Diagnostic gratuit, devis sans engagement.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
            <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
            <Link to="/reparation-volet-roulant-paris-15/" className="text-brand-accent hover:underline">Réparation volet roulant</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/devis-fenetre-paris-15/" className="text-brand-accent hover:underline">Devis gratuit</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline">Zones d'intervention</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}